/**
 * Market-timezone presentation for the Admin (W1-ADMIN, D358). Instants are **stored and carried in UTC**; an operator
 * reads them in the **operating market's IANA timezone** (AGENTS.md). Screens used to slice ISO strings
 * (`createdAt.slice(0, 16)`), which silently showed UTC wall-clock time as if it were local. This module is the one
 * place the Admin turns an instant into text, and the one place an operator's local wall-clock entry (a
 * `datetime-local` value typed in the market's time) becomes a UTC instant.
 *
 * Neutral: no default timezone or locale. When the market's timezone is unknown the instant is still shown, in UTC,
 * with an explicit `UTC` marker, so it can never be mistaken for local time.
 */
import { formatInstant } from '@vello/format';
import { type OperatingMarketView } from '@vello/api-client';
import { marketsOfRegion } from './scope.js';
import { type OperatorScope } from './shell.js';

export type InstantStyle = 'datetime' | 'date' | 'date-short' | 'time' | 'datetime-seconds';

const STYLE: Readonly<Record<InstantStyle, Intl.DateTimeFormatOptions>> = {
  datetime: { dateStyle: 'short', timeStyle: 'short' },
  'datetime-seconds': { dateStyle: 'short', timeStyle: 'medium' },
  date: { dateStyle: 'medium' },
  /** The compact day (`17/09/2026`): for a narrow stat tile, where the month name does not fit. */
  'date-short': { dateStyle: 'short' },
  time: { timeStyle: 'short' },
};

/** True when `tz` is an IANA timezone the runtime can render. */
export function isValidTimeZone(tz: string | undefined): tz is string {
  if (tz === undefined || tz.trim() === '') return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Normalize any parseable instant (`…Z`, `…+00:00`, micro-second fractions) to a canonical UTC `…Z` string. */
export function toUtcInstant(instant: string | undefined): string | undefined {
  if (instant === undefined || instant.trim() === '') return undefined;
  const ms = Date.parse(instant);
  return Number.isNaN(ms) ? undefined : new Date(ms).toISOString();
}

/**
 * Render a stored UTC instant in the market timezone. `'—'` for a missing/unparseable instant. An unknown or invalid
 * timezone renders in UTC **with a `UTC` suffix** (never an unlabeled UTC wall clock).
 */
export function formatMarketInstant(instant: string | undefined, timeZone: string | undefined, locale: string, style: InstantStyle = 'datetime'): string {
  const utc = toUtcInstant(instant);
  if (utc === undefined) return '—';
  const known = isValidTimeZone(timeZone);
  const text = formatInstant(utc, { locale, timeZone: known ? timeZone : 'UTC', measurement: 'metric' }, STYLE[style]);
  return known ? text : `${text} UTC`;
}

/** The IANA timezone of a registered market, or undefined when the catalog does not list a valid one. */
export function marketTimeZone(marketId: string | undefined, markets: readonly OperatingMarketView[]): string | undefined {
  if (marketId === undefined) return undefined;
  const tz = markets.find((m) => m.id === marketId)?.timezone;
  return isValidTimeZone(tz) ? tz : undefined;
}

/**
 * The timezone for a whole operator scope: a city's own timezone; for a state, the timezone every registered market
 * of that state shares; for a country, the timezone every registered market in it shares (undefined when they differ —
 * rows then carry their own market timezone); undefined for global.
 */
export function scopeTimeZone(scope: OperatorScope, markets: readonly OperatingMarketView[]): string | undefined {
  if (scope.city !== undefined) return marketTimeZone(scope.city, markets);
  if (scope.country === undefined) return undefined;
  const inScope = scope.region !== undefined ? marketsOfRegion(scope.region, markets) : markets.filter((m) => m.countryCode.toUpperCase() === scope.country?.toUpperCase() && m.status !== 'RETIRED');
  const zones = new Set(inScope.map((m) => m.timezone));
  const [only] = [...zones];
  return zones.size === 1 && isValidTimeZone(only) ? only : undefined;
}

// ---- wall clock in a timezone ⇄ UTC --------------------------------------------------------------------

const LOCAL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

/** The wall-clock fields of a UTC epoch in a timezone. */
function wallClock(epochMs: number, timeZone: string): { y: number; mo: number; d: number; h: number; mi: number } {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(new Date(epochMs));
  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? 'NaN');
  return { y: get('year'), mo: get('month'), d: get('day'), h: get('hour'), mi: get('minute') };
}

/** The timezone's UTC offset (ms) at an epoch. */
function offsetAt(epochMs: number, timeZone: string): number {
  const w = wallClock(epochMs, timeZone);
  const asUtc = Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi);
  return asUtc - Math.floor(epochMs / 60_000) * 60_000;
}

/**
 * Convert a `datetime-local` value (`YYYY-MM-DDTHH:mm`) typed in the market's wall-clock time to a UTC instant. Returns
 * undefined for a malformed value, an invalid timezone, or a wall-clock time that does not exist in that timezone (a
 * daylight-saving gap). An ambiguous time (a daylight-saving overlap) resolves to its **earlier** instant.
 */
export function zonedLocalToUtc(local: string, timeZone: string): string | undefined {
  const m = LOCAL.exec(local.trim());
  if (m === null || !isValidTimeZone(timeZone)) return undefined;
  const [y, mo, d, h, mi] = m.slice(1).map(Number) as [number, number, number, number, number];
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  const probe = new Date(guess);
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d || h > 23 || mi > 59) return undefined;
  // Candidate instants from the offsets in effect around the guess; keep those whose wall clock matches exactly.
  const offsets = new Set([offsetAt(guess - 86_400_000, timeZone), offsetAt(guess, timeZone), offsetAt(guess + 86_400_000, timeZone)]);
  const matches = [...offsets]
    .map((off) => guess - off)
    .filter((t) => {
      const w = wallClock(t, timeZone);
      return w.y === y && w.mo === mo && w.d === d && w.h === h && w.mi === mi;
    })
    .sort((a, b) => a - b);
  const first = matches[0];
  return first === undefined ? undefined : new Date(first).toISOString();
}

/** A UTC instant as the `datetime-local` value (`YYYY-MM-DDTHH:mm`) of the market's wall clock. */
export function utcToZonedLocal(instant: string, timeZone: string): string | undefined {
  const utc = toUtcInstant(instant);
  if (utc === undefined || !isValidTimeZone(timeZone)) return undefined;
  const w = wallClock(Date.parse(utc), timeZone);
  const p2 = (n: number): string => String(n).padStart(2, '0');
  return `${String(w.y).padStart(4, '0')}-${p2(w.mo)}-${p2(w.d)}T${p2(w.h)}:${p2(w.mi)}`;
}

/**
 * D420: a calendar date (`YYYY-MM-DD`) on the market's wall clock — the default of a date input and the date stamped on
 * an exported file name. An unknown zone falls back to UTC (the input then says nothing more than the UTC date).
 */
export function marketDate(instant: Date | string, timeZone: string | undefined): string {
  const epoch = typeof instant === 'string' ? Date.parse(instant) : instant.getTime();
  const w = wallClock(Number.isNaN(epoch) ? Date.now() : epoch, isValidTimeZone(timeZone) ? timeZone : 'UTC');
  const p2 = (n: number): string => String(n).padStart(2, '0');
  return `${String(w.y).padStart(4, '0')}-${p2(w.mo)}-${p2(w.d)}`;
}

/**
 * D420: the UTC instant a calendar day (`YYYY-MM-DD`, a date input) STARTS on the market's wall clock — its local
 * midnight. An unknown zone keeps the UTC day (the pre-D420 behaviour); a malformed day is undefined.
 */
export function marketDayStart(day: string, timeZone: string | undefined): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day.trim())) return undefined;
  if (!isValidTimeZone(timeZone)) return `${day.trim()}T00:00:00.000Z`;
  const midnight = zonedLocalToUtc(`${day.trim()}T00:00`, timeZone);
  if (midnight !== undefined) return midnight;
  // D420 review W7G-03: a daylight-saving change AT midnight skips 00:00 — the day then starts at its first existing
  // local minute (the end of the gap; gaps are at most a few hours). Never a silent fall-back to the UTC day.
  for (let minute = 1; minute < 6 * 60; minute += 1) {
    const hh = String(Math.floor(minute / 60)).padStart(2, '0');
    const mm = String(minute % 60).padStart(2, '0');
    const first = zonedLocalToUtc(`${day.trim()}T${hh}:${mm}`, timeZone);
    if (first !== undefined) return first;
  }
  return undefined;
}

/** D420: the last millisecond of a calendar day on the market's wall clock (the next local midnight, minus 1 ms). */
export function marketDayEnd(day: string, timeZone: string | undefined): string | undefined {
  const start = marketDayStart(day, 'UTC');
  if (start === undefined) return undefined;
  const next = new Date(Date.parse(start) + 86_400_000).toISOString().substring(0, 10);
  const nextStart = marketDayStart(next, timeZone);
  return nextStart === undefined ? undefined : new Date(Date.parse(nextStart) - 1).toISOString();
}
